// @source EUR-Lex Commission Decision 2000/532/EC — European Waste Catalogue
// Reverse lookup: material / common name -> candidate EWC codes with
// source-context disambiguation (the correct code depends on the chapter the
// waste arises from, not just the material). Every referenced code must
// resolve via resolveEwcSlug — guarded by __tests__/materialEwcLookup.test.ts.
//
// Codes are stored without the hazardous '*' marker; hazard status is read
// from the catalogue entry at render time so it cannot drift from the source.

export interface MaterialCodeContext {
  /** Where the waste arises — decides which code applies. */
  context: string;
  /** EWC code, catalogue spacing, no '*' marker. */
  code: string;
  note?: string;
}

export interface MaterialLookupEntry {
  slug: string;
  name: string;
  synonyms: string[];
  /** Direct answer: most common code + the caveat that matters. */
  summary: string;
  /** Ordered — first entry is the most common assignment. */
  contexts: MaterialCodeContext[];
  /** Optional link to a live /waste-streams/:slug page. */
  wasteStreamSlug?: string;
}

export const MATERIAL_EWC_LOOKUP: MaterialLookupEntry[] = [
  // ---- Packaging (chapter 15) ----
  {
    slug: 'cardboard',
    name: 'Cardboard',
    synonyms: ['corrugated cardboard', 'OCC', 'cardboard boxes', 'carton'],
    summary:
      'Cardboard from commercial or industrial packaging is coded 15 01 01. Cardboard collected in municipal or household streams takes 20 01 01, and rejects from paper recycling plants take 03 03 08.',
    contexts: [
      { context: 'Packaging waste (commercial / industrial)', code: '15 01 01', note: 'The default for baled OCC from retail, logistics and manufacturing' },
      { context: 'Municipal / household separate collection', code: '20 01 01' },
      { context: 'Paper & cardboard recycling rejects', code: '03 03 08' },
    ],
    wasteStreamSlug: 'cardboard-paper',
  },
  {
    slug: 'paper',
    name: 'Paper',
    synonyms: ['office paper', 'mixed paper', 'newspaper', 'shredded paper'],
    summary:
      'Separately collected paper from municipal or office sources is 20 01 01. Paper packaging is 15 01 01, and sorting rejects from paper recycling are 03 03 08.',
    contexts: [
      { context: 'Municipal / office separate collection', code: '20 01 01' },
      { context: 'Paper packaging', code: '15 01 01' },
      { context: 'Paper recycling sorting rejects', code: '03 03 08' },
    ],
    wasteStreamSlug: 'cardboard-paper',
  },
  {
    slug: 'plastic-packaging',
    name: 'Plastic Packaging',
    synonyms: ['plastic wrap', 'shrink wrap', 'plastic bags', 'PET bottles'],
    summary:
      'Plastic packaging — film, bottles, crates — from commercial sources is 15 01 02. Municipal plastics take 20 01 39 and production offcuts from plastics converting take 07 02 13.',
    contexts: [
      { context: 'Packaging waste (commercial / industrial)', code: '15 01 02' },
      { context: 'Municipal separate collection', code: '20 01 39' },
      { context: 'Plastics manufacturing / converting scrap', code: '07 02 13' },
    ],
    wasteStreamSlug: 'plastic-film',
  },
  {
    slug: 'wooden-pallets',
    name: 'Wooden Pallets',
    synonyms: ['pallets', 'wooden packaging', 'crates', 'timber packaging'],
    summary:
      'Wooden pallets and other wooden packaging are 15 01 03. Untreated wood from construction sites is 17 02 01, and municipal wood is 20 01 38.',
    contexts: [
      { context: 'Wooden packaging (pallets, crates)', code: '15 01 03' },
      { context: 'Construction & demolition wood', code: '17 02 01' },
      { context: 'Municipal wood (not containing hazardous substances)', code: '20 01 38' },
    ],
  },
  {
    slug: 'metal-packaging',
    name: 'Metal Packaging',
    synonyms: ['tins', 'cans', 'steel drums', 'aluminium cans'],
    summary:
      'Clean metallic packaging — cans, tins, drums — is 15 01 04. Drums or containers with hazardous residues must be coded 15 01 10 instead.',
    contexts: [
      { context: 'Clean metallic packaging', code: '15 01 04' },
      { context: 'Packaging containing hazardous residues', code: '15 01 10', note: 'Un-triple-rinsed chemical drums and IBCs sit here' },
      { context: 'Municipal metals', code: '20 01 40' },
    ],
  },
  {
    slug: 'composite-packaging',
    name: 'Composite Packaging',
    synonyms: ['tetra pak', 'beverage cartons', 'multi-layer packaging', 'laminates'],
    summary:
      'Multi-material composite packaging such as beverage cartons is 15 01 05. Mixed loads of different packaging types take 15 01 06.',
    contexts: [
      { context: 'Composite / multi-layer packaging', code: '15 01 05' },
      { context: 'Mixed packaging loads', code: '15 01 06' },
    ],
    wasteStreamSlug: 'mixed-packaging',
  },
  {
    slug: 'glass-bottles',
    name: 'Glass Bottles',
    synonyms: ['glass packaging', 'bottle glass', 'container glass'],
    summary:
      'Glass packaging — bottles and jars — is 15 01 07. Municipal collected glass is 20 01 02 and flat glass from construction is 17 02 02.',
    contexts: [
      { context: 'Glass packaging', code: '15 01 07' },
      { context: 'Municipal separate collection', code: '20 01 02' },
      { context: 'Construction & demolition flat glass', code: '17 02 02' },
    ],
    wasteStreamSlug: 'glass-cullet',
  },
  {
    slug: 'contaminated-packaging',
    name: 'Contaminated Packaging',
    synonyms: ['chemical drums', 'empty IBCs', 'paint tins', 'hazardous packaging'],
    summary:
      'Packaging containing residues of, or contaminated by, hazardous substances is 15 01 10 — a hazardous (mirror) entry. Cleaned packaging reverts to the material-specific 15 01 codes.',
    contexts: [
      { context: 'Packaging with hazardous residues', code: '15 01 10' },
      { context: 'Cleaned metallic packaging', code: '15 01 04' },
      { context: 'Cleaned plastic packaging', code: '15 01 02' },
    ],
  },
  {
    slug: 'aerosols',
    name: 'Aerosol Cans',
    synonyms: ['aerosols', 'pressurised cans', 'spray cans'],
    summary:
      'Aerosols with residual propellant or product are usually coded 16 05 04 (hazardous gases in pressure containers). Fully emptied, punctured cans can move to 15 01 04 as metallic packaging.',
    contexts: [
      { context: 'Aerosols with residual contents / propellant', code: '16 05 04' },
      { context: 'Fully emptied and depressurised cans', code: '15 01 04' },
    ],
  },
  {
    slug: 'absorbents-and-rags',
    name: 'Absorbents & Rags',
    synonyms: ['oily rags', 'wipes', 'spill granules', 'absorbent pads', 'filter materials'],
    summary:
      'Absorbents, wipes and rags contaminated by hazardous substances (oils, solvents) are 15 02 02. Uncontaminated absorbents and protective clothing take 15 02 03.',
    contexts: [
      { context: 'Contaminated by oil, solvent or other hazardous substances', code: '15 02 02' },
      { context: 'Uncontaminated absorbents, wipes, protective clothing', code: '15 02 03' },
    ],
  },

  // ---- Municipal (chapter 20) ----
  {
    slug: 'food-waste',
    name: 'Food Waste',
    synonyms: ['kitchen waste', 'catering waste', 'organic waste', 'biowaste'],
    summary:
      'Biodegradable kitchen and canteen waste is 20 01 08. Off-spec or expired product from food manufacturing takes the chapter 02 codes — 02 03 04 for fruit/vegetable lines, 02 02 03 for meat and dairy.',
    contexts: [
      { context: 'Kitchen / canteen / catering waste', code: '20 01 08' },
      { context: 'Food manufacturing — fruit, vegetable, cereal lines', code: '02 03 04' },
      { context: 'Food manufacturing — meat, fish, dairy lines', code: '02 02 03' },
      { context: 'Edible oils and fats', code: '20 01 25' },
    ],
    wasteStreamSlug: 'organic-food-waste',
  },
  {
    slug: 'garden-waste',
    name: 'Garden Waste',
    synonyms: ['green waste', 'landscaping waste', 'grass cuttings', 'tree prunings'],
    summary:
      'Biodegradable garden and park waste — grass, prunings, leaves — is 20 02 01. Soil and stones from garden works take 20 02 02.',
    contexts: [
      { context: 'Biodegradable garden and park waste', code: '20 02 01' },
      { context: 'Soil and stones from gardens / parks', code: '20 02 02' },
    ],
  },
  {
    slug: 'textiles',
    name: 'Textiles',
    synonyms: ['fabric waste', 'clothing', 'clothes', 'rags', 'garments'],
    summary:
      'Separately collected clothes are 20 01 10 and other municipal textiles 20 01 11. Textile industry offcuts take 04 02 22 (processed fibre) and textiles from waste sorting plants 19 12 08.',
    contexts: [
      { context: 'Municipal collection — clothes', code: '20 01 10' },
      { context: 'Municipal collection — other textiles', code: '20 01 11' },
      { context: 'Textile industry — processed fibre offcuts', code: '04 02 22' },
      { context: 'Output of waste sorting / mechanical treatment', code: '19 12 08' },
    ],
    wasteStreamSlug: 'textile-waste',
  },
  {
    slug: 'batteries',
    name: 'Batteries',
    synonyms: ['accumulators', 'battery waste', 'household batteries'],
    summary:
      'Mixed municipal batteries containing lead, Ni-Cd or mercury cells are 20 01 33. By chemistry: lead-acid 16 06 01, Ni-Cd 16 06 02, alkaline 16 06 04, other (including lithium) 16 06 05.',
    contexts: [
      { context: 'Municipal mixed batteries (containing hazardous cells)', code: '20 01 33' },
      { context: 'Lead-acid batteries', code: '16 06 01' },
      { context: 'Ni-Cd batteries', code: '16 06 02' },
      { context: 'Alkaline batteries (except 16 06 03)', code: '16 06 04' },
    ],
  },
  {
    slug: 'lead-acid-batteries',
    name: 'Lead-Acid Batteries',
    synonyms: ['car batteries', 'UPS batteries', 'traction batteries'],
    summary:
      'Lead-acid batteries — automotive, traction and standby — are 16 06 01, a hazardous entry. Mixed municipal battery collections containing them take 20 01 33.',
    contexts: [
      { context: 'Lead-acid batteries (any source)', code: '16 06 01' },
      { context: 'Municipal mixed battery collections', code: '20 01 33' },
    ],
  },
  {
    slug: 'fluorescent-tubes',
    name: 'Fluorescent Tubes',
    synonyms: ['fluorescent lamps', 'strip lights', 'mercury lamps', 'CFL bulbs'],
    summary:
      'Fluorescent tubes and other mercury-containing lamps are 20 01 21, a hazardous entry — they must be kept separate from general WEEE.',
    contexts: [
      { context: 'Fluorescent tubes and mercury-containing lamps', code: '20 01 21' },
      { context: 'Mercury-containing equipment components', code: '16 02 15', note: 'Hazardous components removed from discarded equipment' },
    ],
  },
  {
    slug: 'fridges',
    name: 'Fridges & Freezers',
    synonyms: ['refrigerators', 'freezers', 'chillers', 'air conditioning units'],
    summary:
      'Discarded fridges, freezers and A/C units containing CFCs/HFCs are 20 01 23 from municipal sources, or 16 02 11 from commercial equipment streams — both hazardous entries.',
    contexts: [
      { context: 'Municipal collection — CFC/HFC-containing equipment', code: '20 01 23' },
      { context: 'Commercial / industrial discarded equipment with CFCs', code: '16 02 11' },
    ],
  },
  {
    slug: 'electronic-waste',
    name: 'Electronic Waste',
    synonyms: ['WEEE', 'e-waste', 'computers', 'monitors', 'IT equipment', 'circuit boards'],
    summary:
      'Municipal WEEE with hazardous components is 20 01 35 and without 20 01 36. From commercial streams the mirror pair is 16 02 13 (hazardous components) and 16 02 14.',
    contexts: [
      { context: 'Municipal WEEE containing hazardous components', code: '20 01 35', note: 'CRT monitors, most pre-2006 equipment' },
      { context: 'Municipal WEEE without hazardous components', code: '20 01 36' },
      { context: 'Commercial equipment with hazardous components', code: '16 02 13' },
      { context: 'Commercial equipment without hazardous components', code: '16 02 14' },
    ],
    wasteStreamSlug: 'electronic-waste',
  },
  {
    slug: 'toner-cartridges',
    name: 'Toner Cartridges',
    synonyms: ['printer cartridges', 'ink cartridges', 'toner'],
    summary:
      'Waste printing toner containing hazardous substances is 08 03 17; otherwise 08 03 18. Whole cartridges are commonly managed under these toner codes or as WEEE if electronics are integral.',
    contexts: [
      { context: 'Toner containing hazardous substances', code: '08 03 17' },
      { context: 'Toner other than 08 03 17', code: '08 03 18' },
    ],
  },
  {
    slug: 'paint',
    name: 'Paint',
    synonyms: ['waste paint', 'varnish', 'paint tins with residue', 'coatings'],
    summary:
      'Solvent-based waste paint and varnish is 08 01 11 (hazardous); water-based is 08 01 12. From municipal collections the pair is 20 01 27 / 20 01 28.',
    contexts: [
      { context: 'Manufacturing / trade — containing organic solvents', code: '08 01 11' },
      { context: 'Manufacturing / trade — water-based', code: '08 01 12' },
      { context: 'Municipal — containing hazardous substances', code: '20 01 27' },
      { context: 'Municipal — other paint', code: '20 01 28' },
    ],
    wasteStreamSlug: 'paint-varnish-waste',
  },
  {
    slug: 'medicines',
    name: 'Medicines',
    synonyms: ['pharmaceuticals', 'expired drugs', 'medicine disposal'],
    summary:
      'Cytotoxic and cytostatic medicines are 20 01 31 (hazardous); all other municipal medicines are 20 01 32. Healthcare settings use the chapter 18 equivalents 18 01 08 / 18 01 09.',
    contexts: [
      { context: 'Municipal — cytotoxic / cytostatic medicines', code: '20 01 31' },
      { context: 'Municipal — other medicines', code: '20 01 32' },
      { context: 'Healthcare — cytotoxic / cytostatic', code: '18 01 08' },
      { context: 'Healthcare — other medicines', code: '18 01 09' },
    ],
  },
  {
    slug: 'cooking-oil',
    name: 'Cooking Oil',
    synonyms: ['used cooking oil', 'UCO', 'edible oil', 'fryer oil'],
    summary:
      'Used edible oils and fats from kitchens and catering are 20 01 25. Grease-trap and oil/water separator contents containing only edible oil are 19 08 09.',
    contexts: [
      { context: 'Kitchens / catering — edible oil and fat', code: '20 01 25' },
      { context: 'Grease traps / separators (edible oils only)', code: '19 08 09' },
    ],
  },
  {
    slug: 'bulky-waste',
    name: 'Bulky Waste',
    synonyms: ['mattresses', 'furniture', 'sofas', 'large household items'],
    summary:
      'Bulky municipal items — mattresses, furniture, carpets — are 20 03 07. Wood-dominant furniture can be managed as 20 01 38 when separately collected.',
    contexts: [
      { context: 'Bulky municipal waste', code: '20 03 07' },
      { context: 'Separately collected wood furniture', code: '20 01 38' },
    ],
  },
  {
    slug: 'mixed-municipal-waste',
    name: 'Mixed Municipal Waste',
    synonyms: ['general waste', 'black bag waste', 'residual waste', 'household waste'],
    summary:
      'Mixed, unsegregated municipal waste is 20 03 01 — the general-waste default for household and similar commercial waste.',
    contexts: [
      { context: 'Mixed / residual municipal waste', code: '20 03 01' },
      { context: 'Bulky items', code: '20 03 07' },
      { context: 'Street-cleaning residues', code: '20 03 03' },
    ],
  },
  {
    slug: 'gully-waste',
    name: 'Gully Waste',
    synonyms: ['gully emptyings', 'gulley waste', 'drain arisings', 'gully sludge'],
    summary:
      'Gully emptyings and other waste from sewage and drain cleaning are 20 03 06. Street sweepings collected mechanically take 20 03 03.',
    contexts: [
      { context: 'Gully / drain / sewage cleaning arisings', code: '20 03 06' },
      { context: 'Street sweepings', code: '20 03 03' },
    ],
  },
  {
    slug: 'street-sweepings',
    name: 'Street Sweepings',
    synonyms: ['road sweepings', 'street cleaning residues', 'sweeper waste'],
    summary:
      'Street-cleaning residues — road sweepings and litter-picking arisings — are 20 03 03. Gully emptyings from drain cleaning are coded 20 03 06 instead.',
    contexts: [
      { context: 'Street-cleaning residues', code: '20 03 03' },
      { context: 'Gully / drain cleaning arisings', code: '20 03 06' },
    ],
  },
  {
    slug: 'septic-tank-sludge',
    name: 'Septic Tank Sludge',
    synonyms: ['cesspit waste', 'septic waste', 'tank emptyings'],
    summary:
      'Septic tank sludge is 20 03 04. Sludges from urban waste water treatment plants take 19 08 05.',
    contexts: [
      { context: 'Septic tanks / cesspits', code: '20 03 04' },
      { context: 'Urban waste water treatment plant sludge', code: '19 08 05' },
    ],
  },

  // ---- Construction & demolition (chapter 17) ----
  {
    slug: 'concrete',
    name: 'Concrete',
    synonyms: ['concrete rubble', 'crushed concrete', 'concrete waste'],
    summary:
      'Clean demolition concrete is 17 01 01. Mixed rubble of concrete, bricks and tiles is 17 01 07 — or 17 01 06 if contaminated with hazardous substances. Fresh concrete washout from batching is 10 13 14.',
    contexts: [
      { context: 'Demolition — clean concrete', code: '17 01 01' },
      { context: 'Demolition — mixed concrete/brick/tile rubble', code: '17 01 07' },
      { context: 'Rubble containing hazardous substances', code: '17 01 06' },
      { context: 'Concrete manufacture — washout and sludge', code: '10 13 14' },
    ],
    wasteStreamSlug: 'concrete-masonry',
  },
  {
    slug: 'bricks',
    name: 'Bricks',
    synonyms: ['brick rubble', 'brick waste', 'masonry'],
    summary:
      'Clean waste bricks are 17 01 02. Mixed with concrete and tiles the load becomes 17 01 07, and any hazardous contamination moves it to 17 01 06.',
    contexts: [
      { context: 'Demolition — clean bricks', code: '17 01 02' },
      { context: 'Mixed concrete/brick/tile rubble', code: '17 01 07' },
      { context: 'Rubble containing hazardous substances', code: '17 01 06' },
    ],
    wasteStreamSlug: 'concrete-masonry',
  },
  {
    slug: 'tiles-and-ceramics',
    name: 'Tiles & Ceramics',
    synonyms: ['roof tiles', 'ceramic waste', 'sanitaryware', 'porcelain'],
    summary:
      'Waste tiles and ceramics from construction and demolition are 17 01 03. Mixed rubble loads take 17 01 07 and manufacturing rejects from the ceramics industry take 10 12 08.',
    contexts: [
      { context: 'Demolition — tiles and ceramics', code: '17 01 03' },
      { context: 'Mixed concrete/brick/tile rubble', code: '17 01 07' },
      { context: 'Ceramics manufacture — fired rejects', code: '10 12 08' },
    ],
  },
  {
    slug: 'mixed-construction-waste',
    name: 'Mixed Construction Waste',
    synonyms: ['mixed C&D waste', 'skip waste', 'demolition waste', 'building waste'],
    summary:
      'Mixed construction and demolition waste that cannot be split into single fractions is 17 09 04 — provided it contains no hazardous substances, otherwise 17 09 03. Mixed mineral rubble alone is 17 01 07.',
    contexts: [
      { context: 'Mixed C&D waste (non-hazardous)', code: '17 09 04' },
      { context: 'Mixed C&D waste containing hazardous substances', code: '17 09 03' },
      { context: 'Mixed mineral rubble only', code: '17 01 07' },
    ],
    wasteStreamSlug: 'construction-mixed',
  },
  {
    slug: 'asbestos',
    name: 'Asbestos',
    synonyms: ['asbestos sheets', 'asbestos cement', 'ACM', 'asbestos insulation'],
    summary:
      'Asbestos-containing construction materials — cement sheets, floor tiles — are 17 06 05. Loose asbestos insulation is 17 06 01. Both are hazardous and must be consigned separately.',
    contexts: [
      { context: 'Construction materials containing asbestos', code: '17 06 05', note: 'Bonded ACM: cement sheets, tiles, textured coatings' },
      { context: 'Insulation materials containing asbestos', code: '17 06 01', note: 'Loose fill, lagging, sprayed coatings' },
      { context: 'Discarded equipment containing free asbestos', code: '16 02 12' },
    ],
  },
  {
    slug: 'plasterboard',
    name: 'Plasterboard',
    synonyms: ['drywall', 'gypsum board', 'gib board', 'wallboard'],
    summary:
      'Gypsum-based construction materials including plasterboard are 17 08 02 — or 17 08 01 when contaminated with hazardous substances. Plasterboard must be landfilled separately from biodegradable waste.',
    contexts: [
      { context: 'Gypsum-based construction materials', code: '17 08 02' },
      { context: 'Gypsum materials contaminated with hazardous substances', code: '17 08 01' },
    ],
    wasteStreamSlug: 'gypsum-waste',
  },
  {
    slug: 'wood',
    name: 'Wood',
    synonyms: ['timber', 'untreated wood', 'wood offcuts', 'joinery waste'],
    summary:
      'Construction and demolition wood is 17 02 01. Treated wood containing hazardous preservatives is 17 02 04. Sawmill and joinery offcuts are 03 01 05 and wooden packaging 15 01 03.',
    contexts: [
      { context: 'Construction & demolition wood', code: '17 02 01' },
      { context: 'Treated wood containing hazardous substances', code: '17 02 04', note: 'Creosote, CCA and other preservative-treated timber' },
      { context: 'Wood processing — sawdust, shavings, offcuts', code: '03 01 05' },
      { context: 'Wooden packaging', code: '15 01 03' },
    ],
  },
  {
    slug: 'treated-wood',
    name: 'Treated Wood',
    synonyms: ['creosote timber', 'railway sleepers', 'CCA wood', 'telegraph poles'],
    summary:
      'Wood treated with hazardous preservatives — creosote sleepers, CCA posts — is 17 02 04 from construction/demolition, 20 01 37 from municipal sources, or 19 12 06 out of sorting plants.',
    contexts: [
      { context: 'Construction & demolition — hazardous treated wood', code: '17 02 04' },
      { context: 'Municipal — wood containing hazardous substances', code: '20 01 37' },
      { context: 'Waste sorting output — hazardous wood fraction', code: '19 12 06' },
    ],
  },
  {
    slug: 'flat-glass',
    name: 'Flat Glass',
    synonyms: ['window glass', 'glazing', 'plate glass', 'laminated glass'],
    summary:
      'Flat glass from construction and demolition — windows, glazing — is 17 02 02. Manufacturing cullet from glass production is 10 11 12 and vehicle glass 16 01 20.',
    contexts: [
      { context: 'Construction & demolition glass', code: '17 02 02' },
      { context: 'Glass manufacture — waste glass / cullet', code: '10 11 12' },
      { context: 'End-of-life vehicle glass', code: '16 01 20' },
    ],
    wasteStreamSlug: 'glass-cullet',
  },
  {
    slug: 'asphalt',
    name: 'Asphalt',
    synonyms: ['bituminous mixtures', 'road planings', 'tarmac', 'asphalt millings'],
    summary:
      'Bituminous road-surfacing waste — planings, asphalt — is 17 03 02, provided it contains no coal tar. Tar-bound material (common in pre-1980 roads) is hazardous 17 03 01.',
    contexts: [
      { context: 'Bituminous mixtures without coal tar', code: '17 03 02' },
      { context: 'Bituminous mixtures containing coal tar', code: '17 03 01' },
    ],
  },
  {
    slug: 'soil-and-stones',
    name: 'Soil & Stones',
    synonyms: ['excavated soil', 'subsoil', 'muckaway', 'spoil'],
    summary:
      'Excavated soil and stones free of contamination are 17 05 04. Soil containing hazardous substances is 17 05 03. Garden and park soil takes 20 02 02.',
    contexts: [
      { context: 'Excavation — clean soil and stones', code: '17 05 04' },
      { context: 'Soil containing hazardous substances', code: '17 05 03' },
      { context: 'Garden / park soil and stones', code: '20 02 02' },
    ],
  },
  {
    slug: 'dredging-spoil',
    name: 'Dredging Spoil',
    synonyms: ['dredgings', 'sediment', 'marine spoil'],
    summary:
      'Dredging spoil free of hazardous substances is 17 05 06; contaminated dredgings are 17 05 05.',
    contexts: [
      { context: 'Dredging spoil (non-hazardous)', code: '17 05 06' },
      { context: 'Dredging spoil containing hazardous substances', code: '17 05 05' },
    ],
  },
  {
    slug: 'track-ballast',
    name: 'Track Ballast',
    synonyms: ['railway ballast', 'rail aggregate'],
    summary:
      'Railway track ballast is 17 05 08 when clean, or 17 05 07 when contaminated with hazardous substances such as oils or herbicides.',
    contexts: [
      { context: 'Track ballast (non-hazardous)', code: '17 05 08' },
      { context: 'Track ballast containing hazardous substances', code: '17 05 07' },
    ],
  },
  {
    slug: 'insulation',
    name: 'Insulation',
    synonyms: ['rockwool', 'glass wool', 'mineral wool', 'PIR boards', 'loft insulation'],
    summary:
      'Insulation materials from construction are 17 06 04 — unless they contain asbestos (17 06 01) or other hazardous substances (17 06 03).',
    contexts: [
      { context: 'Insulation materials (non-hazardous)', code: '17 06 04' },
      { context: 'Insulation containing asbestos', code: '17 06 01' },
      { context: 'Insulation containing other hazardous substances', code: '17 06 03' },
    ],
  },
  {
    slug: 'cables',
    name: 'Cables',
    synonyms: ['electrical cable', 'copper cable', 'wiring', 'armoured cable'],
    summary:
      'Cables from construction and demolition are 17 04 11 — unless contaminated with oil, coal tar or other hazardous substances, which makes them 17 04 10.',
    contexts: [
      { context: 'Cables (non-hazardous)', code: '17 04 11' },
      { context: 'Cables containing oil, coal tar or hazardous substances', code: '17 04 10' },
    ],
  },
  {
    slug: 'copper',
    name: 'Copper',
    synonyms: ['copper scrap', 'bronze', 'brass', 'copper pipe'],
    summary:
      'Copper, bronze and brass from construction and demolition are 17 04 01. Machining filings and turnings are 12 01 03 and municipal metals 20 01 40.',
    contexts: [
      { context: 'Construction & demolition — copper, bronze, brass', code: '17 04 01' },
      { context: 'Machining — non-ferrous filings and turnings', code: '12 01 03' },
      { context: 'Municipal metals', code: '20 01 40' },
    ],
  },
  {
    slug: 'aluminium',
    name: 'Aluminium',
    synonyms: ['aluminium scrap', 'ali scrap', 'aluminium profiles', 'aluminium sheet'],
    summary:
      'Aluminium from construction and demolition is 17 04 02. Machining swarf is 12 01 03, clean cans and foil packaging 15 01 04, and municipal metals 20 01 40.',
    contexts: [
      { context: 'Construction & demolition aluminium', code: '17 04 02' },
      { context: 'Machining — non-ferrous filings and turnings', code: '12 01 03' },
      { context: 'Metallic packaging', code: '15 01 04' },
    ],
  },
  {
    slug: 'lead',
    name: 'Lead',
    synonyms: ['lead scrap', 'lead flashing', 'lead sheet'],
    summary:
      'Lead from construction and demolition — flashing, pipe, sheet — is 17 04 03. Lead-acid batteries are a separate hazardous stream, 16 06 01.',
    contexts: [
      { context: 'Construction & demolition lead', code: '17 04 03' },
      { context: 'Lead-acid batteries', code: '16 06 01' },
    ],
  },
  {
    slug: 'zinc',
    name: 'Zinc',
    synonyms: ['zinc scrap', 'galvanised offcuts', 'zinc sheet'],
    summary:
      'Zinc from construction and demolition is 17 04 04. Mixed metal loads containing zinc take 17 04 07; zinc-bearing galvanising residues sit in chapter 11.',
    contexts: [
      { context: 'Construction & demolition zinc', code: '17 04 04' },
      { context: 'Mixed metals including zinc', code: '17 04 07' },
    ],
  },
  {
    slug: 'steel',
    name: 'Iron & Steel',
    synonyms: ['steel scrap', 'iron scrap', 'rebar', 'structural steel', 'stainless steel'],
    summary:
      'Iron and steel from construction and demolition are 17 04 05. Machining filings and turnings are 12 01 01, end-of-life vehicle ferrous metal 16 01 17, and sorted ferrous metal from waste plants 19 12 02.',
    contexts: [
      { context: 'Construction & demolition iron and steel', code: '17 04 05' },
      { context: 'Machining — ferrous filings and turnings', code: '12 01 01' },
      { context: 'End-of-life vehicles — ferrous metal', code: '16 01 17' },
      { context: 'Waste sorting output — ferrous metal', code: '19 12 02' },
    ],
    wasteStreamSlug: 'mixed-metal-scrap',
  },
  {
    slug: 'mixed-metals',
    name: 'Mixed Metals',
    synonyms: ['mixed scrap', 'light iron', 'mixed metal skip'],
    summary:
      'Mixed metals from construction and demolition are 17 04 07. Municipal mixed metals are 20 01 40 and mixed non-ferrous from sorting plants 19 12 03.',
    contexts: [
      { context: 'Construction & demolition mixed metals', code: '17 04 07' },
      { context: 'Municipal metals', code: '20 01 40' },
      { context: 'Waste sorting output — non-ferrous metal', code: '19 12 03' },
    ],
    wasteStreamSlug: 'mixed-metal-scrap',
  },
  {
    slug: 'tin',
    name: 'Tin',
    synonyms: ['tin scrap', 'tinplate'],
    summary:
      'Tin from construction and demolition is 17 04 06. Tinplate packaging — cans and tins — is 15 01 04.',
    contexts: [
      { context: 'Construction & demolition tin', code: '17 04 06' },
      { context: 'Tinplate packaging', code: '15 01 04' },
    ],
  },
  {
    slug: 'gypsum',
    name: 'Gypsum',
    synonyms: ['plaster', 'gypsum waste', 'plaster skim'],
    summary:
      'Gypsum-based construction materials — plasterboard, plaster — are 17 08 02, or 17 08 01 when contaminated with hazardous substances.',
    contexts: [
      { context: 'Gypsum-based construction materials', code: '17 08 02' },
      { context: 'Contaminated gypsum materials', code: '17 08 01' },
    ],
    wasteStreamSlug: 'gypsum-waste',
  },

  // ---- Oils, solvents & vehicle fluids (chapters 13, 14, 16) ----
  {
    slug: 'engine-oil',
    name: 'Engine Oil',
    synonyms: ['used oil', 'waste oil', 'lube oil', 'motor oil', 'gear oil'],
    summary:
      'Used mineral engine, gear and lubricating oil (non-chlorinated) is 13 02 05 — the standard code for garage waste oil. Synthetic oils are 13 02 06 and readily biodegradable oils 13 02 07.',
    contexts: [
      { context: 'Mineral-based, non-chlorinated engine/gear/lube oil', code: '13 02 05' },
      { context: 'Synthetic engine, gear and lubricating oils', code: '13 02 06' },
      { context: 'Other engine, gear and lubricating oils', code: '13 02 08' },
    ],
    wasteStreamSlug: 'lubricant-oil',
  },
  {
    slug: 'hydraulic-oil',
    name: 'Hydraulic Oil',
    synonyms: ['hydraulic fluid', 'hydraulic oils'],
    summary:
      'Mineral-based non-chlorinated hydraulic oil is 13 01 10. Synthetic hydraulic oils are 13 01 11 and other hydraulic oils 13 01 13 — all hazardous entries.',
    contexts: [
      { context: 'Mineral-based, non-chlorinated hydraulic oil', code: '13 01 10' },
      { context: 'Synthetic hydraulic oils', code: '13 01 11' },
      { context: 'Other hydraulic oils', code: '13 01 13' },
    ],
    wasteStreamSlug: 'lubricant-oil',
  },
  {
    slug: 'oily-water',
    name: 'Oily Water',
    synonyms: ['interceptor waste', 'oil water mix', 'separator contents'],
    summary:
      'Oily water from oil/water separators and interceptors is 13 05 07. Separator sludges are 13 05 02 and solids from grit chambers 13 05 01 — all hazardous.',
    contexts: [
      { context: 'Oil/water separators — oily water', code: '13 05 07' },
      { context: 'Oil/water separators — sludges', code: '13 05 02' },
      { context: 'Grit chamber / separator solids', code: '13 05 01' },
    ],
  },
  {
    slug: 'waste-fuel',
    name: 'Waste Fuel',
    synonyms: ['contaminated diesel', 'waste petrol', 'off-spec fuel', 'fuel oil'],
    summary:
      'Waste fuel oil and diesel are 13 07 01, waste petrol 13 07 02, and other fuels or mixtures 13 07 03 — all hazardous entries.',
    contexts: [
      { context: 'Fuel oil and diesel', code: '13 07 01' },
      { context: 'Petrol', code: '13 07 02' },
      { context: 'Other fuels including blends', code: '13 07 03' },
    ],
  },
  {
    slug: 'solvents',
    name: 'Solvents',
    synonyms: ['waste solvent', 'thinners', 'degreaser', 'gun wash'],
    summary:
      'Waste organic solvents from cleaning and degreasing are coded in chapter 14: halogenated solvents 14 06 02, other solvent mixtures 14 06 03. Municipal solvents are 20 01 13.',
    contexts: [
      { context: 'Halogenated solvents and mixtures', code: '14 06 02' },
      { context: 'Other solvents and solvent mixtures', code: '14 06 03' },
      { context: 'Municipal collection — solvents', code: '20 01 13' },
    ],
    wasteStreamSlug: 'spent-solvents',
  },
  {
    slug: 'antifreeze',
    name: 'Antifreeze',
    synonyms: ['coolant', 'glycol', 'engine coolant'],
    summary:
      'Antifreeze containing hazardous substances is 16 01 14; uncontaminated glycol coolant is 16 01 15. Most used automotive coolant is consigned as 16 01 14.',
    contexts: [
      { context: 'Antifreeze containing hazardous substances', code: '16 01 14' },
      { context: 'Antifreeze other than 16 01 14', code: '16 01 15' },
    ],
  },
  {
    slug: 'brake-fluid',
    name: 'Brake Fluid',
    synonyms: ['brake fluids', 'DOT fluid'],
    summary:
      'Waste brake fluid is 16 01 13 — a hazardous entry in the end-of-life vehicle chapter, used regardless of whether it arises from servicing or dismantling.',
    contexts: [
      { context: 'Brake fluids (servicing or vehicle dismantling)', code: '16 01 13' },
    ],
  },
  {
    slug: 'oil-filters',
    name: 'Oil Filters',
    synonyms: ['fuel filters', 'vehicle filters'],
    summary:
      'Used oil filters from vehicles and plant are 16 01 07 — a hazardous entry due to residual oil content.',
    contexts: [
      { context: 'Oil filters (vehicles and plant)', code: '16 01 07' },
    ],
  },
  {
    slug: 'tyres',
    name: 'Tyres',
    synonyms: ['end-of-life tyres', 'waste tyres', 'ELT', 'rubber tyres'],
    summary:
      'End-of-life tyres are 16 01 03 — the single code for car, truck and plant tyres regardless of source.',
    contexts: [
      { context: 'End-of-life tyres (all vehicle types)', code: '16 01 03' },
    ],
  },
  {
    slug: 'end-of-life-vehicles',
    name: 'End-of-Life Vehicles',
    synonyms: ['ELV', 'scrap cars', 'vehicle disposal'],
    summary:
      'Undepolluted end-of-life vehicles are hazardous 16 01 04. Once fluids, batteries and other hazardous components are removed, the depolluted shell is 16 01 06.',
    contexts: [
      { context: 'ELVs before depollution', code: '16 01 04' },
      { context: 'Depolluted ELVs (no liquids or hazardous components)', code: '16 01 06' },
      { context: 'Ferrous metal from dismantling', code: '16 01 17' },
    ],
  },
  {
    slug: 'gas-cylinders',
    name: 'Gas Cylinders',
    synonyms: ['pressure containers', 'LPG bottles', 'compressed gas', 'helium cylinders'],
    summary:
      'Gases in pressure containers containing hazardous gases are 16 05 04; other gases in pressure containers — including inert gases such as helium — are 16 05 05.',
    contexts: [
      { context: 'Pressure containers with hazardous gases (incl. halons)', code: '16 05 04' },
      { context: 'Pressure containers with non-hazardous gases', code: '16 05 05' },
    ],
  },
  {
    slug: 'laboratory-chemicals',
    name: 'Laboratory Chemicals',
    synonyms: ['lab chemicals', 'reagents', 'chemical disposal', 'smalls'],
    summary:
      'Laboratory chemicals consisting of or containing hazardous substances, including mixtures, are 16 05 06. Discarded non-hazardous chemicals take 16 05 09.',
    contexts: [
      { context: 'Laboratory chemicals (hazardous, incl. mixtures)', code: '16 05 06' },
      { context: 'Discarded chemicals (non-hazardous)', code: '16 05 09' },
    ],
  },
  {
    slug: 'spent-catalysts',
    name: 'Spent Catalysts',
    synonyms: ['used catalysts', 'catalyst waste', 'PGM catalysts', 'hydroprocessing catalysts'],
    summary:
      'Spent catalysts are coded by metal content in chapter 16 08: precious-metal catalysts 16 08 01, hazardous transition-metal catalysts 16 08 02, other transition-metal catalysts 16 08 03.',
    contexts: [
      { context: 'Containing gold, silver, platinum-group metals', code: '16 08 01' },
      { context: 'Hazardous transition metals or compounds', code: '16 08 02', note: 'Most refinery hydroprocessing catalysts (Ni-Mo, Co-Mo) after use' },
      { context: 'Other transition metals (non-hazardous)', code: '16 08 03' },
      { context: 'Contaminated with dangerous substances', code: '16 08 07' },
    ],
    wasteStreamSlug: 'spent-catalysts',
  },
  {
    slug: 'refractory',
    name: 'Refractory Linings',
    synonyms: ['furnace linings', 'kiln bricks', 'refractory bricks'],
    summary:
      'Spent linings and refractories from metallurgical processes are 16 11 04 (or 16 11 03 if hazardous); from non-metallurgical processes 16 11 06 (or 16 11 05 if hazardous).',
    contexts: [
      { context: 'Metallurgical processes (non-hazardous)', code: '16 11 04' },
      { context: 'Metallurgical processes (hazardous)', code: '16 11 03' },
      { context: 'Non-metallurgical processes (non-hazardous)', code: '16 11 06' },
    ],
  },

  // ---- Manufacturing & process wastes ----
  {
    slug: 'swarf',
    name: 'Swarf & Turnings',
    synonyms: ['metal turnings', 'machining swarf', 'filings', 'chips'],
    summary:
      'Ferrous metal filings and turnings from machining are 12 01 01; non-ferrous 12 01 03. Machining dusts take 12 01 02 (ferrous) and 12 01 04 (non-ferrous).',
    contexts: [
      { context: 'Machining — ferrous filings and turnings', code: '12 01 01' },
      { context: 'Machining — non-ferrous filings and turnings', code: '12 01 03' },
      { context: 'Machining — ferrous dust and particles', code: '12 01 02' },
    ],
  },
  {
    slug: 'machining-coolant',
    name: 'Machining Coolant',
    synonyms: ['cutting fluid', 'metalworking emulsion', 'suds', 'coolant emulsion'],
    summary:
      'Spent halogen-free machining emulsions and solutions are 12 01 09 — a hazardous entry. Halogen-containing emulsions are 12 01 08 and neat machining oils 12 01 07 or 12 01 10.',
    contexts: [
      { context: 'Halogen-free machining emulsions', code: '12 01 09' },
      { context: 'Halogen-containing machining emulsions', code: '12 01 08' },
      { context: 'Halogen-free machining oils (mineral)', code: '12 01 07' },
    ],
  },
  {
    slug: 'grinding-sludge',
    name: 'Grinding Sludge',
    synonyms: ['machining sludge', 'metal sludge', 'honing sludge'],
    summary:
      'Machining sludges containing hazardous substances (typically oil) are 12 01 14; other machining sludges 12 01 15. Grinding-body residues take 12 01 20 / 12 01 21.',
    contexts: [
      { context: 'Machining sludges containing hazardous substances', code: '12 01 14' },
      { context: 'Machining sludges (non-hazardous)', code: '12 01 15' },
      { context: 'Spent grinding bodies containing hazardous substances', code: '12 01 20' },
    ],
  },
  {
    slug: 'blasting-grit',
    name: 'Blasting Grit',
    synonyms: ['shot blast media', 'abrasive media', 'garnet', 'blast residue'],
    summary:
      'Spent blasting material containing hazardous substances — from removing leaded paint or contaminated coatings — is 12 01 16; clean spent media is 12 01 17.',
    contexts: [
      { context: 'Blasting material containing hazardous substances', code: '12 01 16' },
      { context: 'Blasting material (non-hazardous)', code: '12 01 17' },
    ],
  },
  {
    slug: 'sawdust',
    name: 'Sawdust & Shavings',
    synonyms: ['wood shavings', 'wood dust', 'wood chips', 'offcuts'],
    summary:
      'Sawdust, shavings and offcuts from wood processing are 03 01 05 — unless the wood contains hazardous preservatives, which makes them 03 01 04.',
    contexts: [
      { context: 'Wood processing — clean sawdust, shavings, offcuts', code: '03 01 05' },
      { context: 'Containing hazardous substances (treated wood)', code: '03 01 04' },
    ],
  },
  {
    slug: 'bark',
    name: 'Bark & Cork',
    synonyms: ['bark waste', 'cork waste', 'forestry residues'],
    summary:
      'Waste bark and cork from wood processing are 03 01 01. Bark and wood arising at pulp and paper mills take 03 03 01.',
    contexts: [
      { context: 'Wood processing — bark and cork', code: '03 01 01' },
      { context: 'Pulp & paper production — bark and wood', code: '03 03 01' },
    ],
  },
  {
    slug: 'paper-sludge',
    name: 'Paper Sludge',
    synonyms: ['de-inking sludge', 'fibre sludge', 'mill sludge'],
    summary:
      'De-inking sludge from paper recycling is 03 03 05. Fibre and coating sludges from mechanical separation are 03 03 10 and rejects from recycling 03 03 08.',
    contexts: [
      { context: 'Paper recycling — de-inking sludge', code: '03 03 05' },
      { context: 'Mechanical separation — fibre and coating sludge', code: '03 03 10' },
      { context: 'Paper & cardboard recycling rejects', code: '03 03 08' },
    ],
  },
  {
    slug: 'leather-waste',
    name: 'Leather Waste',
    synonyms: ['leather offcuts', 'tannery waste', 'hide waste'],
    summary:
      'Tanned leather waste — shavings, cuttings, buffing dust — is 04 01 08. Untanned hide waste (fleshings, lime split) is 04 01 01.',
    contexts: [
      { context: 'Tanned leather — shavings, cuttings, dust', code: '04 01 08' },
      { context: 'Untanned hides — fleshings and lime split', code: '04 01 01' },
    ],
  },
  {
    slug: 'textile-offcuts',
    name: 'Textile Offcuts',
    synonyms: ['fabric offcuts', 'fibre waste', 'yarn waste', 'cutting-room waste'],
    summary:
      'Processed textile fibre waste from manufacturing is 04 02 22; unprocessed fibre is 04 02 21. Municipal textiles take 20 01 11.',
    contexts: [
      { context: 'Textile industry — processed fibres', code: '04 02 22' },
      { context: 'Textile industry — unprocessed fibres', code: '04 02 21' },
      { context: 'Municipal textiles', code: '20 01 11' },
    ],
    wasteStreamSlug: 'textile-waste',
  },
  {
    slug: 'fly-ash',
    name: 'Fly Ash',
    synonyms: ['PFA', 'pulverised fuel ash', 'combustion ash'],
    summary:
      'Coal-fired power station fly ash is 10 01 02. Peat and untreated-wood fly ash is 10 01 03, oil fly ash 10 01 04, and incinerator fly ash 19 01 13 or 19 01 14.',
    contexts: [
      { context: 'Coal combustion — fly ash', code: '10 01 02' },
      { context: 'Peat / untreated wood combustion', code: '10 01 03' },
      { context: 'Waste incineration — fly ash containing hazardous substances', code: '19 01 13' },
    ],
  },
  {
    slug: 'bottom-ash',
    name: 'Bottom Ash',
    synonyms: ['IBA', 'incinerator bottom ash', 'furnace ash', 'slag ash'],
    summary:
      'Power-plant bottom ash and boiler slag are 10 01 01. Incinerator bottom ash is 19 01 12 — or 19 01 11 if it contains hazardous substances.',
    contexts: [
      { context: 'Power plants — bottom ash and boiler slag', code: '10 01 01' },
      { context: 'Waste incineration — bottom ash (non-hazardous)', code: '19 01 12' },
      { context: 'Waste incineration — bottom ash containing hazardous substances', code: '19 01 11' },
    ],
  },
  {
    slug: 'steel-slag',
    name: 'Steel Slag',
    synonyms: ['blast furnace slag', 'BOS slag', 'EAF slag', 'furnace slag'],
    summary:
      'Unprocessed slag from iron and steel making is 10 02 02; wastes from slag processing are 10 02 01. Non-ferrous smelting slags sit in 10 04 to 10 08 by metal.',
    contexts: [
      { context: 'Iron & steel — unprocessed slag', code: '10 02 02' },
      { context: 'Iron & steel — slag processing wastes', code: '10 02 01' },
    ],
    wasteStreamSlug: 'steel-slag',
  },
  {
    slug: 'mill-scale',
    name: 'Mill Scale',
    synonyms: ['rolling scale', 'oxide scale', 'scale'],
    summary:
      'Mill scale from hot rolling of steel is 10 02 10 — an iron-oxide stream widely recycled back into sinter plants and cement.',
    contexts: [
      { context: 'Steel hot rolling — mill scale', code: '10 02 10' },
    ],
    wasteStreamSlug: 'mill-scale',
  },
  {
    slug: 'foundry-sand',
    name: 'Foundry Sand',
    synonyms: ['casting sand', 'moulding sand', 'spent foundry sand'],
    summary:
      'Ferrous foundry casting cores and moulds after pouring are 10 09 08 — or 10 09 07 if they contain hazardous substances. Non-ferrous equivalents are 10 10 08 / 10 10 07.',
    contexts: [
      { context: 'Ferrous foundries — cores and moulds after casting', code: '10 09 08' },
      { context: 'Ferrous foundries — containing hazardous substances', code: '10 09 07' },
      { context: 'Non-ferrous foundries — cores and moulds after casting', code: '10 10 08' },
    ],
  },
  {
    slug: 'glass-cullet',
    name: 'Glass Cullet',
    synonyms: ['waste glass', 'crushed glass', 'cullet'],
    summary:
      'Waste glass from glass manufacture is 10 11 12 (or 10 11 11 if containing heavy metals). Sorted glass from waste treatment is 19 12 05, packaging glass 15 01 07.',
    contexts: [
      { context: 'Glass manufacture — waste glass', code: '10 11 12' },
      { context: 'Waste sorting output — glass', code: '19 12 05' },
      { context: 'Glass packaging', code: '15 01 07' },
    ],
    wasteStreamSlug: 'glass-cullet',
  },
  {
    slug: 'adhesives-and-sealants',
    name: 'Adhesives & Sealants',
    synonyms: ['glue waste', 'mastic', 'sealant cartridges', 'resin waste'],
    summary:
      'Waste adhesives and sealants containing organic solvents or other hazardous substances are 08 04 09; water-based or cured products are 08 04 10.',
    contexts: [
      { context: 'Containing organic solvents / hazardous substances', code: '08 04 09' },
      { context: 'Other adhesives and sealants', code: '08 04 10' },
    ],
  },
  {
    slug: 'ink',
    name: 'Ink',
    synonyms: ['printing ink', 'ink waste', 'flexo ink'],
    summary:
      'Waste printing ink containing hazardous substances (solvent-based) is 08 03 12; water-based ink is 08 03 13. Ink sludges take 08 03 14 / 08 03 15.',
    contexts: [
      { context: 'Ink containing hazardous substances', code: '08 03 12' },
      { context: 'Ink other than 08 03 12', code: '08 03 13' },
    ],
  },
  {
    slug: 'clinical-waste',
    name: 'Clinical Waste',
    synonyms: ['healthcare waste', 'infectious waste', 'sharps', 'medical waste'],
    summary:
      'Infectious healthcare waste is 18 01 03 — the main hazardous clinical stream. Sharps are 18 01 01, non-infectious items (dressings, PPE) 18 01 04.',
    contexts: [
      { context: 'Infectious waste (special collection/disposal requirements)', code: '18 01 03' },
      { context: 'Sharps (except 18 01 03)', code: '18 01 01' },
      { context: 'Non-infectious dressings, linen, PPE', code: '18 01 04' },
    ],
  },
  {
    slug: 'animal-by-products',
    name: 'Animal By-Products',
    synonyms: ['animal tissue', 'ABP', 'slaughterhouse waste'],
    summary:
      'Animal-tissue waste from agriculture is 02 01 02 and from meat, fish and dairy processing 02 02 02. Note ABP Regulation (EC) 1069/2009 controls apply in parallel to EWC coding.',
    contexts: [
      { context: 'Agriculture — animal-tissue waste', code: '02 01 02' },
      { context: 'Meat / fish / dairy processing — animal tissue', code: '02 02 02' },
      { context: 'Materials unsuitable for consumption or processing', code: '02 02 03' },
    ],
  },
  {
    slug: 'manure',
    name: 'Manure & Slurry',
    synonyms: ['animal faeces', 'slurry', 'farmyard manure'],
    summary:
      'Animal faeces, urine and manure — including spoiled straw and separately collected effluent — are 02 01 06.',
    contexts: [
      { context: 'Livestock farming — manure, slurry, effluent', code: '02 01 06' },
    ],
  },
  {
    slug: 'sewage-sludge',
    name: 'Sewage Sludge',
    synonyms: ['biosolids', 'WWTP sludge', 'treatment sludge'],
    summary:
      'Sludges from urban waste water treatment are 19 08 05. Industrial biological-treatment sludges are 19 08 12 — or 19 08 11 if they contain hazardous substances.',
    contexts: [
      { context: 'Urban waste water treatment', code: '19 08 05' },
      { context: 'Industrial effluent — biological treatment sludge', code: '19 08 12' },
      { context: 'Industrial sludge containing hazardous substances', code: '19 08 11' },
    ],
    wasteStreamSlug: 'etp-sludge',
  },
  {
    slug: 'polystyrene',
    name: 'Polystyrene',
    synonyms: ['EPS', 'styrofoam', 'expanded polystyrene', 'XPS'],
    summary:
      'EPS packaging is 15 01 02. Polystyrene insulation from demolition is 17 06 04, and production scrap from plastics converting 07 02 13.',
    contexts: [
      { context: 'Packaging EPS', code: '15 01 02' },
      { context: 'Demolition — insulation boards', code: '17 06 04' },
      { context: 'Plastics manufacturing scrap', code: '07 02 13' },
    ],
  },
  {
    slug: 'pvc',
    name: 'PVC',
    synonyms: ['vinyl', 'uPVC', 'PVC windows', 'vinyl flooring'],
    summary:
      'PVC from construction and demolition — window profiles, pipes, flooring — is 17 02 03. Manufacturing scrap is 07 02 13 and sorted plastics from waste plants 19 12 04.',
    contexts: [
      { context: 'Construction & demolition plastic', code: '17 02 03' },
      { context: 'Plastics manufacturing scrap', code: '07 02 13' },
      { context: 'Waste sorting output — plastic and rubber', code: '19 12 04' },
    ],
  },
  {
    slug: 'carpet',
    name: 'Carpet',
    synonyms: ['carpet tiles', 'floor coverings', 'underlay'],
    summary:
      'Carpet from households is bulky waste 20 03 07. Fitting offcuts from commercial sources are commonly 04 02 22 (processed textile fibre) or 19 12 08 after sorting.',
    contexts: [
      { context: 'Household / municipal — bulky waste', code: '20 03 07' },
      { context: 'Textile manufacturing offcuts', code: '04 02 22' },
      { context: 'Waste sorting output — textiles', code: '19 12 08' },
    ],
  },
  {
    slug: 'rubber',
    name: 'Rubber',
    synonyms: ['rubber waste', 'rubber offcuts', 'conveyor belts', 'seals'],
    summary:
      'Tyres are 16 01 03. Other rubber — belts, seals, offcuts — is usually 07 02 99 from manufacturing, 19 12 04 from sorting plants, or 16 01 99 from vehicle dismantling.',
    contexts: [
      { context: 'End-of-life tyres', code: '16 01 03' },
      { context: 'Waste sorting output — plastic and rubber', code: '19 12 04' },
      { context: 'Rubber manufacturing — wastes not otherwise specified', code: '07 02 99' },
    ],
  },
  {
    slug: 'srf-rdf',
    name: 'SRF / RDF',
    synonyms: ['refuse-derived fuel', 'solid recovered fuel', 'combustible waste'],
    summary:
      'Combustible waste produced by mechanical treatment — refuse-derived fuel and solid recovered fuel — is 19 12 10.',
    contexts: [
      { context: 'Mechanical treatment output — combustible fraction', code: '19 12 10' },
      { context: 'Other mechanical treatment residues', code: '19 12 12' },
    ],
  },
  {
    slug: 'compost-oversize',
    name: 'Compost Oversize',
    synonyms: ['compost rejects', 'screening oversize', 'off-spec compost'],
    summary:
      'Non-composted fractions of municipal and similar waste from composting plants are 19 05 01. Off-specification compost is 19 05 03.',
    contexts: [
      { context: 'Composting — non-composted municipal fraction', code: '19 05 01' },
      { context: 'Off-specification compost', code: '19 05 03' },
    ],
  },
];

/** Lookup by URL slug. */
export function getMaterialLookupEntry(slug: string): MaterialLookupEntry | undefined {
  return MATERIAL_EWC_LOOKUP.find(m => m.slug === slug);
}

function normalizeCode(code: string): string {
  return code.replace(/\*/g, '').trim().replace(/\s+/g, ' ');
}

/** Reverse index: which materials reference a given EWC code (any context). */
export function getMaterialsForEwcCode(code: string): MaterialLookupEntry[] {
  const target = normalizeCode(code);
  return MATERIAL_EWC_LOOKUP.filter(m =>
    m.contexts.some(c => normalizeCode(c.code) === target),
  );
}

const TITLE_SUFFIX = ' | SymbioFlows';

/**
 * SERP title, exact-matching the "ewc code for X" query pattern.
 *
 * Keeps the answer in the title (featured-snippet play) but, when the
 * material classifies under more than one code, teases the exceptions —
 * GSC 2026-07-10: /ewc-code-for/plastic-packaging at position 3.6 with 70
 * impressions and 0 clicks because the title fully answered the query.
 * @source https://developers.google.com/search/docs/appearance/title-link
 */
export function materialTitle(entry: MaterialLookupEntry): string {
  let base = `EWC Code for ${entry.name} — ${entry.contexts[0].code}`;
  if (entry.contexts.length > 1 && base.length + ' & Exceptions'.length <= 60) {
    base += ' & Exceptions';
  }
  return base.length + TITLE_SUFFIX.length <= 70 ? base + TITLE_SUFFIX : base;
}

/** SERP meta description within budget; always names the primary code. */
export function materialMetaDescription(entry: MaterialLookupEntry): string {
  const lead = `The EWC code for ${entry.name.toLowerCase()} is ${entry.contexts[0].code} in most cases. `;
  const full = lead + entry.summary;
  if (full.length <= 230) return full;
  const cut = full.slice(0, 230);
  return cut.slice(0, cut.lastIndexOf(' ')).replace(/[,;:.]$/, '');
}

